import { Core } from "@faire/design-tokens";
import { trackProductInfoEcoFriendlyInformationLearnMoreCtaClick } from "@faire/web-api/events/productInfo/click/ecoFriendlyInformationLearnMoreCta";
import { trackProductInfoPdpEcoFriendlyInfoShowDetailsClick } from "@faire/web-api/events/productInfo/click/pdpEcoFriendlyInfoShowDetails";
import { trackProductInfoEcoFriendlyInformationAccordionViewView } from "@faire/web-api/events/productInfo/view/ecoFriendlyInformationAccordionView";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { useStrictLocalization } from "@faire/web/common/localization";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Button } from "@faire/web/slate/Button";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { Suspense } from "@faire/web/ui/Suspense";
import { observer } from "mobx-react";
import React, { useState } from "react";
import styled from "styled-components";

import { FormattedText } from "@faire/retailer/components/FormattedText";
import { AccordionContainer } from "@faire/retailer/views/product/ProductDescriptionAccordions/AccordionContainer";
import { LazyEcoFriendlyInfoAccordionDetailsModal } from "@faire/retailer/views/product/ProductDescriptionAccordions/LazyEcoFriendlyInfoAccordionDetailsModal";
import { StyledIconByName } from "@faire/retailer/views/product/utils/StyledIconByName";

interface IProps {
  className?: string;
  ecoFriendlyInfoSection:
    | IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection
    | undefined;
}

const ProductEcoFriendlyInfoAccordionBase: React.FC<IProps> = ({
  ecoFriendlyInfoSection,
}) => {
  const { strictLocalize } = useStrictLocalization();
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const initialNumberOfLinesShown = 3;

  const title: string = strictLocalize({
    defaultMessage: "Eco-friendly information",
    description: {
      text: "Section title. Text of button to expand the eco-friendly information of the product (eg; Product materials, Packaging, and Production",
      img: "https://cdn.faire.com/fastly/9fc5b4b98e1e51910ceb3a9a33caaa457f6b624783265a9cc921e057f6c17899.png",
    },
  });

  const ecoFriendlyInfoText = ecoFriendlyInfoSection?.entries
    .flatMap((section) => {
      return section.text?.translated ?? section.text?.original;
    })
    .filter(isNotUndefined);

  const ecoFriendlyAttributeFirstRowAttribute =
    ecoFriendlyInfoSection?.entries[0];

  const ecoFriendlyAttributeFirstRowEntry = ecoFriendlyInfoText?.[0];

  if (
    !ecoFriendlyAttributeFirstRowAttribute ||
    !ecoFriendlyAttributeFirstRowEntry
  ) {
    return null;
  }

  const learnMoreCTA = () => {
    if (
      !(
        ecoFriendlyInfoSection?.external_link?.text &&
        ecoFriendlyInfoSection?.external_link?.url
      )
    ) {
      return undefined;
    }

    return (
      <>
        <Spacer width={12} />
        <Button
          variant="tertiary"
          linkTo={ecoFriendlyInfoSection.external_link?.url}
          target="_blank"
          style={{ minWidth: "76px", whiteSpace: "nowrap" }}
          onClick={() =>
            trackProductInfoEcoFriendlyInformationLearnMoreCtaClick()
          }
        >
          <Typography color={Core.text.enabled}>
            {ecoFriendlyInfoSection.external_link?.text}
          </Typography>
        </Button>
      </>
    );
  };

  return (
    <>
      <AccordionContainer
        title={title}
        maxNumberOfLinesShownWhenCollapsed={initialNumberOfLinesShown}
        isCustomizedCollapsableComponent={true}
        collapsedComponent={collapsedInfoRow(
          ecoFriendlyAttributeFirstRowAttribute,
          ecoFriendlyAttributeFirstRowEntry
        )}
        expandedComponent={ProductEcoFriendlyInfo(
          ecoFriendlyInfoSection,
          setOpenDetailsModal
        )}
        onView={trackProductInfoEcoFriendlyInformationAccordionViewView}
      />
      <Suspense fallback={null}>
        <LazyEcoFriendlyInfoAccordionDetailsModal
          ecoFriendlyInfoSection={ecoFriendlyInfoSection}
          onClose={() => setOpenDetailsModal(false)}
          isOpen={openDetailsModal}
          learnMoreCTA={learnMoreCTA()}
        />
      </Suspense>
    </>
  );
};

const collapsedInfoRow = (
  ecoFriendlyAttributeFirstRowInfo: IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection.IProductInformationSubSectionEntry,
  ecoFriendlyInfoSectionEntry: string
) => {
  return (
    <Row>
      <IconWrapper>
        <StyledIconByName
          name={ecoFriendlyAttributeFirstRowInfo.slate_icon?.type}
          fontSize="16px"
          color={Core.icon.default}
          strokeWidth={1}
        />
      </IconWrapper>
      <Typography>
        <FormattedText text={ecoFriendlyInfoSectionEntry + "..."} />
      </Typography>
    </Row>
  );
};

const ProductEcoFriendlyInfo = (
  ecoFriendlyInfoSection: IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection,
  openDetailsModalHandler: (value: boolean) => void
) => (
  <>
    {ecoFriendlyInfoSection?.entries.map((ecoFriendlyAttributeDetail) => {
      return EcoFriendlyAttributeInfoRow(ecoFriendlyAttributeDetail);
    })}
    {EcoFriendlyDetailsModalCTA(openDetailsModalHandler)}
  </>
);

const EcoFriendlyAttributeInfoRow = (
  ecoFriendlyAttributeDetail: IProductPageResponse.IPdpProduct.IDetails.IProductInformationSubSection.IProductInformationSubSectionEntry
) => {
  const attributeEntry =
    ecoFriendlyAttributeDetail.text?.translated ??
    ecoFriendlyAttributeDetail.text?.original;
  return (
    <ExpandedInfoRowWrapper
      key={`eco-friendly-attribute-${ecoFriendlyAttributeDetail.slate_icon?.type}`}
    >
      <IconWrapper>
        <StyledIconByName
          name={ecoFriendlyAttributeDetail.slate_icon?.type}
          fontSize="16px"
          color={Core.icon.default}
          strokeWidth={1}
        />
      </IconWrapper>
      <Typography>
        <FormattedText text={attributeEntry} />
      </Typography>
    </ExpandedInfoRowWrapper>
  );
};

const EcoFriendlyDetailsModalCTA = (
  openDetailsModalHandler: (value: boolean) => void
) => {
  return (
    <Button
      variant="tertiary"
      onClick={(event) => {
        openDetailsModalHandler(true);
        trackProductInfoPdpEcoFriendlyInfoShowDetailsClick();
        // Stop propagate click event to accordion container
        event.stopPropagation();
        event.preventDefault();
      }}
    >
      <Typography color={Core.text.enabled}>
        <StrictLocalMsg
          defaultMessage="Show details"
          description={{
            text: "Button to show details modal of the eco-friendly attributes of the product",
            img: "https://cdn.faire.com/fastly/342841d7078860a99c7ef4d52def5e22a169f4c7d00d10cb7a90fc59c95be645.png",
          }}
        />
      </Typography>
    </Button>
  );
};

export const ProductEcoFriendlyInfoAccordion = observer(
  ProductEcoFriendlyInfoAccordionBase
);

const ExpandedInfoRowWrapper = styled(Row)`
  margin-bottom: ${getSpacing("1x")};
  ${responsiveMediaQueries.mobile} {
    margin-bottom: 12px;
  }
`;

const IconWrapper = styled(Column)`
  margin: 2px 12px 0 0;
`;
